import * as React from 'react';
import { Box, Button, Link as MuiLink, Typography } from '@mui/material';
import { useLogout, useNotify } from 'react-admin';
import { Link } from 'react-router-dom';

import { apiFetch } from '../api/httpClient';

export type LicenseRenewalInfo = {
    vendorName: string;
    phone?: string | null;
    email?: string | null;
    websiteUrl?: string | null;
    extraNotes?: string | null;
};

export function LicenseRenewalPage() {
    const notify = useNotify();
    const [info, setInfo] = React.useState<LicenseRenewalInfo | null>(null);

    React.useEffect(() => {
        let alive = true;
        void (async () => {
            try {
                const res = await apiFetch('/api/auth/license-renewal-info', { method: 'GET' }, { auth: true });
                if (!alive) return;
                if (!res.ok) {
                    notify('Could not load renewal information', { type: 'warning' });
                    return;
                }
                const j = (await res.json()) as LicenseRenewalInfo;
                setInfo(j);
            } catch {
                if (alive) notify('Could not load renewal information', { type: 'warning' });
            }
        })();
        return () => {
            alive = false;
        };
    }, [notify]);

    const v = info?.vendorName ?? 'Corbis Soft Pvt Ltd';

    return (
        <Box sx={{ maxWidth: 560, mx: 'auto', p: 3 }}>
            <Typography variant="h5" gutterBottom>
                License renewal
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Contact {v} to renew your software license.
            </Typography>
            {info?.phone ? (
                <Typography variant="body2">
                    Phone:{' '}
                    <MuiLink href={`tel:${info.phone}`} underline="hover">
                        {info.phone}
                    </MuiLink>
                </Typography>
            ) : null}
            {info?.email ? (
                <Typography variant="body2">
                    Email:{' '}
                    <MuiLink href={`mailto:${info.email}`} underline="hover">
                        {info.email}
                    </MuiLink>
                </Typography>
            ) : null}
            {info?.websiteUrl ? (
                <Typography variant="body2">
                    Website:{' '}
                    <MuiLink href={info.websiteUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                        {info.websiteUrl}
                    </MuiLink>
                </Typography>
            ) : null}
            {info?.extraNotes ? (
                <Typography variant="body2" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
                    {info.extraNotes}
                </Typography>
            ) : null}
            <Button component={Link} to="/license-blocked" variant="outlined" sx={{ mt: 2 }}>
                Back
            </Button>
        </Box>
    );
}

export function LicenseBlockedPage() {
    const logout = useLogout();

    return (
        <Box
            sx={{
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 2,
                py: 4,
            }}
        >
            <Typography variant="h5" gutterBottom sx={{ maxWidth: 520 }}>
                Your license has expired. Please contact Corbis Soft Pvt Ltd to unlock your software.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', mt: 2 }}>
                <Button component={Link} to="/license-renewal" variant="contained" color="primary">
                    Renewal information
                </Button>
                <Button component={Link} to="/self-profile" variant="outlined">
                    Profile
                </Button>
                <Button
                    variant="text"
                    onClick={() => {
                        void logout();
                    }}
                >
                    Logout
                </Button>
            </Box>
        </Box>
    );
}

export default LicenseBlockedPage;
